import Papa from 'papaparse';
import type { CSVRow, Question, QuestionOption } from '@/types';
import { QuestionTheme } from '@/types';
import { CSV_URL } from '@config/constants';
import { QuestionsMetaParser } from './questions-meta-parser';

export class CSVParser {
  private static instance: CSVParser;
  private cache: Map<string, Question[]> = new Map();
  private cacheTimeout = 3600000; // 1 hour

  /**
   * Singleton pattern to ensure only one instance
   */
  static getInstance(): CSVParser {
    if (!CSVParser.instance) {
      CSVParser.instance = new CSVParser();
    }
    return CSVParser.instance;
  }

  /**
   * Fetch and parse questions from CSV
   * IMPORTANT: This processes ALL rows from the CSV dynamically
   */
  async fetchQuestions(): Promise<Question[]> {
    try {
      // Check cache first
      const cached = this.cache.get('questions');
      if (cached) {
        console.log('📦 Returning cached questions:', cached.length);
        return cached;
      }

      // Load questions metadata first
      await QuestionsMetaParser.loadQuestionsMeta();

      console.log('📥 Fetching CSV from:', CSV_URL);
      
      // Utiliser le cache si disponible
      const CacheService = (await import('./cache-service')).CacheService;
      const response = await CacheService.fetchWithCache(CSV_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const csvText = await response.text();
      const questions = await this.parseCSV(csvText);

      // Cache results
      this.cache.set('questions', questions);
      setTimeout(() => {
        console.log('🗑️ Clearing cache after 1 hour');
        this.cache.delete('questions');
      }, this.cacheTimeout);

      return questions;
    } catch (error) {
      console.error('❌ Error fetching CSV:', error);
      throw new Error('Impossible de charger le questionnaire');
    }
  }

  /**
   * Parse CSV text into questions
   */
  private parseCSV(csvText: string): Promise<Question[]> {
    return new Promise((resolve, reject) => {
      Papa.parse<CSVRow>(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          console.log(`📊 CSV parsed: ${results.data.length} rows found`);
          const questions = this.transformToQuestions(results.data);
          console.log(`✅ ${questions.length} questions generated dynamically`);
          resolve(questions);
        },
        error: (error: Error) => {
          console.error('❌ Papa Parse error:', error);
          reject(error);
        },
      });
    });
  }

  /**
   * Transform CSV rows to Question objects
   * IMPORTANT: ALL rows are processed, no filtering
   */
  private transformToQuestions(rows: CSVRow[]): Question[] {
    // Group rows by question
    const grouped = new Map<string, CSVRow[]>();

    rows.forEach(row => {
      if (!row.Question) {
        console.warn('⚠️ Row without question ignored:', row);
        return;
      }

      if (!grouped.has(row.Question)) {
        grouped.set(row.Question, []);
      }
      grouped.get(row.Question)!.push(row);
    });

    // Transform each group into a Question
    const questions: Question[] = [];

    grouped.forEach((options, questionText) => {
      const firstRow = options[0];
      const questionId = this.generateQuestionId(questionText);
      
      // Debug displayCondition parsing
      console.log(`🔍 Question "${questionText}" - affichage: "${firstRow.affichage}"`);

      const question = {
        id: questionId,
        code: firstRow.code || questionId,
        title: questionText,
        description: QuestionsMetaParser.getSubtitle(questionText) || firstRow.texte_brique || '',
        emoji: QuestionsMetaParser.getEmoji(questionText),
        theme: this.detectTheme(firstRow.code),
        isMultiple: this.detectIfMultipleChoice(firstRow.code),
        options: this.createOptions(options),
        // Aggregate all products from all options
        products: this.aggregateProducts(options),
        exclusions: this.aggregateExclusions(options),
        alternatives: this.aggregateAlternatives(options),
        points: parseInt(firstRow.points) || 0,
        displayCondition: (firstRow.affichage === 'condition' ? 'condition' : 'all') as 'all' | 'condition',
        // Don't set activatedQuestions at question level - it's per option
      };
      
      questions.push(question);
    });

    console.log(
      '📋 Questions detected:',
      questions.map(q => `\n  - ${q.title} (${q.options.length} options, display: ${q.displayCondition})`).join('')
    );
    
    // Log conditional questions details
    const conditionalQuestions = questions.filter(q => q.displayCondition === 'condition');
    console.log(`🔒 Conditional questions: ${conditionalQuestions.length}`, 
      conditionalQuestions.map(q => q.title)
    );

    return questions;
  }

  /**
   * Create options from CSV rows
   */
  private createOptions(rows: CSVRow[]): QuestionOption[] {
    return rows
      .filter(row => row.titre_brique) // Only if there's option text
      .map((row, index) => {
        const activatedQuestions = this.parseActivatedQuestions(row.activate);
        if (activatedQuestions.length > 0) {
          console.log(`🔑 Option "${row.titre_brique}" activates:`, activatedQuestions);
        }
        
        const exclusions = this.parseExclusionsFromRow(row);
        const alternatives = this.parseAlternativesFromRow(row);
        
        if (exclusions.length > 0) {
          console.log(`🚫 Option "${row.titre_brique}" excludes:`, exclusions);
          console.log(`   ↔️ Alternatives:`, alternatives);
        }
        
        const optionId = row.code || `option-${index}`;
        const products = row.produits_concaténés
          ? row.produits_concaténés.split(' | ').filter(p => p)
          : [];
        
        
        return {
          id: optionId,
          value: optionId,  // Use consistent ID as value
          label: row.titre_brique,
          code: row.code,
          // Data specific to this option
          products,
          exclusions,
          alternatives,
          points: parseInt(row.points) || 0, // Points for this specific option
          activatedQuestions,
        };
      });
  }

  /**
   * Detect theme from code
   * Automatically detects new themes based on prefix
   */
  private detectTheme(code: string): QuestionTheme {
    if (!code) return QuestionTheme.AUTRE;

    // Known prefixes mapping
    const knownPrefixes: Record<string, QuestionTheme> = {
      SOPK: QuestionTheme.SOPK,
      TD: QuestionTheme.TROUBLES_DIGESTIFS,
      SPM: QuestionTheme.SPM,
      R: QuestionTheme.REGLES,
      FX: QuestionTheme.FLUX,
      DR: QuestionTheme.DOULEURS_RAPPORTS,
      F: QuestionTheme.FATIGUE,
      P: QuestionTheme.PATHOLOGIES,
      A: QuestionTheme.ALIMENTATION,
      SPORT: QuestionTheme.SPORT,
    };

    for (const [prefix, theme] of Object.entries(knownPrefixes)) {
      if (code.startsWith(prefix)) return theme;
    }

    // New theme detected!
    const newTheme = code.match(/^([A-Z]+)/)?.[1]?.toLowerCase() || 'autre';
    console.log(`🆕 New theme detected: ${newTheme} (code: ${code})`);
    return QuestionTheme.AUTRE;
  }

  /**
   * Detect if question allows multiple choices
   */
  private detectIfMultipleChoice(_code: string): boolean {
    // TOUTES les questions sont à choix multiples
    // Comme spécifié : chaque choix peut apporter des points
    return true;
  }

  /**
   * Detect emoji based on theme
   * @deprecated Now using QuestionsMetaParser.getEmoji() instead
   */
  // private detectEmoji(code: string): string {
  //   const emojiMap: Record<string, string> = {
  //     SOPK: '🌸',
  //     TD: '🤰',
  //     SPM: '😓',
  //     R: '🩸',
  //     FX: '💧',
  //     DR: '💑',
  //     F: '😴',
  //     P: '🏥',
  //     A: '🥗',
  //     SPORT: '🏃‍♀️',
  //     SLEEP: '😴',
  //     STRESS: '😰',
  //   };

  //   for (const [prefix, emoji] of Object.entries(emojiMap)) {
  //     if (code?.startsWith(prefix)) return emoji;
  //   }

  //   return '💜'; // Default emoji
  // }

  /**
   * Generate unique ID for a question
   */
  private generateQuestionId(questionText: string): string {
    return questionText
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Parse activated questions from activate column
   */
  private parseActivatedQuestions(activateStr: string): string[] {
    if (!activateStr || activateStr.trim() === '') return [];
    
    // Split by | and trim each question
    return activateStr
      .split('|')
      .map(q => q.trim())
      .filter(q => q);
  }

  /**
   * Parse exclusions from row
   * The Exclusions column contains products to exclude separated by " | "
   */
  private parseExclusionsFromRow(row: CSVRow): string[] {
    if (!row.Exclusions || row.Exclusions.trim() === '') return [];
    
    // Simply split by | to get all products to exclude
    return row.Exclusions.split(' | ').map(e => e.trim()).filter(e => e);
  }

  /**
   * Parse alternatives from row
   * If a row has exclusions, produits_concaténés contains the replacement products
   */
  private parseAlternativesFromRow(row: CSVRow): Record<string, string> {
    const alternatives: Record<string, string> = {};
    
    // Only process if there are exclusions
    if (!row.Exclusions || row.Exclusions.trim() === '') return alternatives;
    
    // Get the products to exclude
    const exclusions = this.parseExclusionsFromRow(row);
    
    // Get the replacement products from produits_concaténés
    const replacements = row.produits_concaténés ? 
      row.produits_concaténés.split(' | ').map(p => p.trim()).filter(p => p) : 
      [];
    
    // Map each excluded product to the replacement products
    exclusions.forEach(excludedProduct => {
      if (replacements.length > 0) {
        alternatives[excludedProduct] = replacements.join(' | ');
      }
    });
    
    return alternatives;
  }


  /**
   * Aggregate all products from options
   */
  private aggregateProducts(rows: CSVRow[]): string[] {
    const products = new Set<string>();

    rows.forEach(row => {
      if (row.produits_concaténés) {
        row.produits_concaténés
          .split(' | ')
          .filter(p => p)
          .forEach(p => products.add(p));
      }
    });

    return Array.from(products);
  }

  /**
   * Aggregate all exclusions from options
   */
  private aggregateExclusions(rows: CSVRow[]): string[] {
    const exclusions = new Set<string>();

    rows.forEach(row => {
      const rowExclusions = this.parseExclusionsFromRow(row);
      rowExclusions.forEach(e => exclusions.add(e));
    });

    return Array.from(exclusions);
  }

  /**
   * Aggregate all alternatives from options
   */
  private aggregateAlternatives(rows: CSVRow[]): Record<string, string> {
    const alternatives: Record<string, string> = {};

    rows.forEach(row => {
      const rowAlternatives = this.parseAlternativesFromRow(row);
      Object.entries(rowAlternatives).forEach(([exclusion, replacement]) => {
        alternatives[exclusion] = replacement;
      });
    });

    return alternatives;
  }

  /**
   * Clear cache manually if needed
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ Cache cleared manually');
  }
}
